import { Spinner } from '@primer/react';
import { CheckStreamlineCoreLine } from '@upright/ui/icons';
import styled from 'styled-components';
import {
  EmptyHeader,
  ModalDialog,
  ModalContent,
  ModalIconWrapper,
  ModalTitle,
  ModalDescription,
  ModalButton,
} from './shared-modal-components';

interface UpgradeConfirmingPaymentModalProps {
  isOpen: boolean;
  timedOut: boolean;
  onClose: () => void;
}

/**
 * Shown immediately after Stripe redirect while we poll for the webhook
 * to confirm the payment. Transitions to UpgradeSuccessModal on success.
 */
export function UpgradeConfirmingPaymentModal({ isOpen, timedOut, onClose }: UpgradeConfirmingPaymentModalProps) {
  if (!isOpen) return null;

  return (
    <ModalDialog title="Confirming payment" onClose={onClose} width="medium" renderHeader={EmptyHeader}>
      <ModalContent>
        {timedOut ? (
          <>
            <WarningIconWrapper>
              <CheckStreamlineCoreLine width={48} height={48} color="var(--color__yellow-600)" />
            </WarningIconWrapper>
            <ModalTitle>Payment is being processed</ModalTitle>
            <ModalDescription>
              Your payment was received but is still being processed. Please close this dialog and refresh the page in a
              moment. If the issue persists, contact Upright support.
            </ModalDescription>
            <ModalButton onClick={onClose} variant="primary" block>
              Close
            </ModalButton>
          </>
        ) : (
          <>
            <Spinner size="large" />
            <ModalTitle>Confirming your payment</ModalTitle>
            <ModalDescription>
              Please wait while we confirm your payment. This usually takes a few seconds.
            </ModalDescription>
          </>
        )}
      </ModalContent>
    </ModalDialog>
  );
}

interface UpgradeSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UpgradeSuccessModal({ isOpen, onClose }: UpgradeSuccessModalProps) {
  if (!isOpen) return null;

  return (
    <ModalDialog title="Upgrade successful" onClose={onClose} width="medium" renderHeader={EmptyHeader}>
      <ModalContent>
        <SuccessIconWrapper>
          <CheckStreamlineCoreLine width={48} height={48} color="var(--color__teal-600)" />
        </SuccessIconWrapper>
        <ModalTitle>Upgrade to Pro tier successful</ModalTitle>
        <ModalDescription>
          Your assessment has been upgraded to the Pro tier. You now have full access to all Pro features for the next
          12 months.
        </ModalDescription>
        <ModalButton onClick={onClose} variant="primary" block>
          Continue
        </ModalButton>
      </ModalContent>
    </ModalDialog>
  );
}

const SuccessIconWrapper = styled(ModalIconWrapper)`
  background: var(--color__teal-100);
`;

const WarningIconWrapper = styled(ModalIconWrapper)`
  background: var(--color__yellow-100);
`;
